import React from 'react';
import { ArrowLeft, Lock, Shield, Eye, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PublicHeader } from '../PublicHeader';
import { Footer } from '../Footer';

export function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-gradient-to-br from-servet-dark-blue to-servet-blue rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>
            
            <div className="relative text-center text-white">
              <div className="flex items-center justify-center mb-6">
                <Lock className="h-12 w-12 text-servet-red" />
              </div>
              <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
              <p className="text-white/90">
                Last Updated: February 3, 2025
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  Welcome to <strong>BidWin by SerVet Digital LLC</strong> ("BidWin," "we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our platform at <strong>bidwin.servetdigital.com</strong> (the "Platform").
                </p>
                <p className="text-gray-600">
                  By using BidWin, you consent to the data practices described in this policy.
                </p>
              </div>
            </section>

            {/* Information Collection */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 mr-2 text-servet-blue" />
                2. Information We Collect
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">We collect the following types of information:</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>Account Information</strong>: When you sign up, we collect your name, email, phone number, and business details.</li>
                  <li><strong>Profile Data</strong>: Information about your company, capabilities, NAICS codes, and past performance.</li>
                  <li><strong>Contract Interactions</strong>: Records of contracts you view, save, or bid on.</li>
                  <li><strong>AI Usage Data</strong>: Information about AI-generated proposals and contract matching interactions.</li>
                  <li><strong>Payment Information</strong>: Processed securely through third-party payment providers.</li>
                  <li><strong>Device & Log Data</strong>: IP address, browser type, and usage patterns for security and analytics.</li>
                </ul>
                <p className="text-gray-600 font-medium">
                  We <strong>do not collect</strong> sensitive personal information such as Social Security numbers or government-issued IDs.
                </p>
              </div>
            </section>

            {/* Information Usage */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 mr-2 text-servet-blue" />
                3. How We Use Your Information
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">We use collected data to:</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li>Provide and improve BidWin's services.</li>
                  <li>Generate AI-powered contract proposals.</li>
                  <li>Match users with government contract opportunities.</li>
                  <li>Personalize user experience and recommendations.</li>
                  <li>Process transactions and subscription payments.</li>
                  <li>Enforce security and compliance with our <strong>Terms of Service</strong>.</li>
                </ul>
              </div>
            </section>

            {/* Information Sharing */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. How We Share Information
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600 font-medium">We <strong>do not sell or rent your data</strong>. However, we may share data in the following cases:</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>With Service Providers</strong>: Third-party tools for payment processing, AI services, or analytics.</li>
                  <li><strong>For Legal Compliance</strong>: If required by law, regulation, or legal process.</li>
                  <li><strong>For Business Transfers</strong>: In case of a merger, acquisition, or sale of assets.</li>
                </ul>
              </div>
            </section>

            {/* AI Data Handling */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. AI Data Handling & User Responsibility
              </h2>
              <div className="prose prose-blue max-w-none">
                <ul className="list-disc pl-6 text-gray-600">
                  <li>AI-generated proposals are stored to <strong>improve service accuracy</strong>.</li>
                  <li>Users are <strong>responsible for reviewing AI-generated content</strong> before submission.</li>
                  <li>AI data is <strong>not shared with third parties</strong> outside of BidWin's platform.</li>
                </ul>
              </div>
            </section>

            {/* Security Measures */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Data Security Measures
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  We use <strong>encryption, firewalls, and access controls</strong> to protect user data. However, <strong>no system is 100% secure</strong>, so users should also take security precautions.
                </p>
              </div>
            </section>

            {/* Privacy Choices */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Your Privacy Choices
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">You can:</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>Access & Update Your Data</strong>: Modify account and profile information anytime.</li>
                  <li><strong>Request Data Deletion</strong>: Contact <strong>support@servetdigital.com</strong> to request removal.</li>
                  <li><strong>Opt-Out of AI Processing</strong>: Disable AI-based recommendations in settings.</li>
                </ul>
              </div>
            </section>

            {/* Additional Sections */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Additional Information
              </h2>
              <div className="prose prose-blue max-w-none space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-gray-900">8. Data Retention</h3>
                  <p className="text-gray-600">
                    We retain your data <strong>as long as necessary</strong> for business operations, compliance, and legal obligations.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-900">9. International Data Transfers</h3>
                  <p className="text-gray-600">
                    If you access BidWin outside the <strong>United States</strong>, your data may be processed in the U.S. We comply with applicable data protection laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-900">10. Children's Privacy</h3>
                  <p className="text-gray-600">
                    BidWin is <strong>not intended for children under 18</strong>. We do not knowingly collect data from minors.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-900">11. Changes to this Privacy Policy</h3>
                  <p className="text-gray-600">
                    We may update this policy periodically. We will notify users of significant changes via email or platform notifications.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-900">12. Contact Us</h3>
                  <p className="text-gray-600">
                    For privacy inquiries, contact <strong>support@servetdigital.com</strong>.
                  </p>
                </div>
              </div>
            </section>

            {/* Agreement */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <p className="text-center text-gray-600 font-medium">
                By using BidWin, you agree to this Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}